import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

/**
 * useReconciliationAlert
 *
 * Watches the paired Sun/Moon dial. When gate_delta transitions from
 * non-zero to zero — the moment the pair reconciles — fires a single
 * subtle toast. Silent on load; only fires on the transition edge.
 *
 * Polls GET /api/cosmic-clock/status at `pollMs` (default 60s).
 * Reconciled windows last ~2h (1 gate at solar-hand speed), so we
 * suppress repeats inside the same window with a ref-guard.
 */
export default function useReconciliationAlert({ pollMs = 60000 } = {}) {
  const wasReconciledRef = useRef(null); // null · unknown until first fetch

  useEffect(() => {
    let cancelled = false;

    const check = () => {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/cosmic-clock/status`)
        .then((r) => {
          if (cancelled) return;
          const reconciled = !!r.data?.pair?.reconciled;
          const prev = wasReconciledRef.current;
          if (prev === false && reconciled === true) {
            const solarGate = r.data?.solar?.gate;
            const hemi = r.data?.solar?.hemisphere;
            toast.success("Cosmic clock · paired hands reconciled", {
              description: `Δ = 0 · gate ${solarGate} · ${hemi} hemisphere`,
              duration: 8000,
              className: "sentinel-reconciled-toast",
            });
          }
          wasReconciledRef.current = reconciled;
        })
        .catch(() => {
          /* silent — the console never breaks because the dial hiccuped */
        });
    };

    check();
    const iv = setInterval(check, pollMs);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [pollMs]);
}
